import { IChartData } from '@ankr.com/telemetry';

import { StatsTimeframe } from 'modules/stats/types';
import { getStatsTimeBorders } from 'modules/stats/utils/getStatsTimeBorders';
import { statsTimeframeToIntervalMap } from 'modules/stats/const';
import { formatUnixSecondsToMilliseconds } from 'modules/common/utils/getDateFromUnixSeconds';

interface IMapStatsForRequestsWidgetProps {
  data: IChartData[];
  timeframe: StatsTimeframe;
  timeframeShift?: number;
  shouldHideLastPointIfSmall?: boolean;
}

const LAST_POINT_THRESHOLD = 0.5; // Threshold for filtering out sharp drops in the last data point
const MINIMUM_POINTS_REQUIRED = 2; // Minimum data points required to evaluate the drop logic

export const mapStatsForRequestsWidget = ({
  data,
  shouldHideLastPointIfSmall = false,
  timeframe,
  timeframeShift = 0,
}: IMapStatsForRequestsWidgetProps): IChartData[] => {
  if (!data?.length) return [];

  // Get aligned time borders and shift (e.g., timezone offset or custom shift)
  const { endDate, shift, startDate } = getStatsTimeBorders({
    timeframe,
    timeframeShift,
  });

  const intervalMs = formatUnixSecondsToMilliseconds(
    statsTimeframeToIntervalMap[timeframe],
  );

  // Align the start and end times to the nearest interval
  const alignedStart =
    Math.floor((startDate.getTime() + shift) / intervalMs) * intervalMs;
  const alignedEnd =
    Math.floor((endDate.getTime() + shift) / intervalMs) * intervalMs;

  // Pre-calculate the expected number of data points
  const numPoints = Math.floor((alignedEnd - alignedStart) / intervalMs) + 1;

  if (numPoints <= 0) return [];

  // Create a map of timestamp -> value for fast lookup
  const dataMap = new Map<number, number>(
    data.map(({ time, value }) => [time.getTime(), Number(value)]),
  );

  // Fill in the full range of time-aligned data points with either real or zero values
  const allPoints = Array.from({ length: numPoints }, (_, i) => {
    const timestamp = alignedStart + i * intervalMs;

    return {
      time: new Date(timestamp),
      value: dataMap.get(timestamp) ?? 0, // Default to 0 if no data for this timestamp
    };
  });

  // Optionally hide the last data point if it dropped too sharply compared to the previous one
  if (
    shouldHideLastPointIfSmall &&
    allPoints.length >= MINIMUM_POINTS_REQUIRED
  ) {
    const [prev, last] = allPoints.slice(-2);

    if (prev.value > 0 && last.value < prev.value * LAST_POINT_THRESHOLD) {
      return allPoints.slice(0, -1); // Remove last point
    }
  }

  return allPoints;
};
